import { available, isPositiveU64, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

export interface MonotonicOutputQuote<T> {
  outputAmountD6: bigint;
  value: T;
}

export interface MinimumInputForOutput<T> {
  targetOutputD6: bigint;
  maximumInputD6: bigint;
  quoteAtInput: (inputD6: bigint) => PoolMathResult<MonotonicOutputQuote<T>>;
}

/** Binary-searches an integer exact-input quote and preserves its native rounding. */
export function solveMinimumInputForOutput<T>(input: MinimumInputForOutput<T>): PoolMathResult<T> {
  if (!isPositiveU64(input.targetOutputD6) || !isPositiveU64(input.maximumInputD6)) {
    return unavailable('invalid_amount');
  }

  const maximumQuote = input.quoteAtInput(input.maximumInputD6);
  if (!maximumQuote.available) return maximumQuote;
  if (maximumQuote.value.outputAmountD6 < input.targetOutputD6) {
    return unavailable('insufficient_liquidity');
  }

  let low = 1n;
  let high = input.maximumInputD6;
  while (low < high) {
    const candidateInput = (low + high) / 2n;
    const candidateQuote = input.quoteAtInput(candidateInput);
    if (!candidateQuote.available) {
      if (
        candidateQuote.reason !== 'amount_below_minimum' &&
        candidateQuote.reason !== 'zero_progress'
      ) {
        return candidateQuote;
      }
      low = candidateInput + 1n;
    } else if (candidateQuote.value.outputAmountD6 >= input.targetOutputD6) {
      high = candidateInput;
    } else {
      low = candidateInput + 1n;
    }
  }

  const result = input.quoteAtInput(low);
  return result.available ? available(result.value.value) : result;
}
